import { initFileBrowser } from './files.js';
import { initAI } from './ai.js';
import { initSectionNav } from './section-nav.js';
import { initVersionBadge } from './version-badge.js';
import { checkAuth, login, logout, getCurrentUser, isRepoOwner, isGodMode } from './auth.js';
import { openProfileModal } from './settings.js';
import { initCommentsPanel } from './comments.js';
import { apiPost } from './api.js';

// Initialize app
async function initApp() {
  // Check if user is authenticated
  const authenticated = await checkAuth();
  
  if (authenticated) {
    // Hide login, show app
    document.getElementById('loginOverlay').classList.add('hidden');
    // Initialize app modules
    initAppAfterAuth();
  } else {
    // Show login overlay (it's visible by default)
    // Set up login form handler
    setupLoginForm();
  }
}

// Set up login form
async function setupLoginForm() {
  const form = document.getElementById('loginForm');
  const errorDiv = document.getElementById('loginError');
  const submitBtn = document.getElementById('loginButton');
  
  // Check if bootstrap is available (no users exist)
  await checkBootstrapStatus();
  
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    
    const username = document.getElementById('username').value;
    const password = document.getElementById('password').value;
    
    // Disable button while loading
    submitBtn.disabled = true;
    submitBtn.textContent = 'Signing in...';
    errorDiv.classList.remove('visible');
    
    const result = await login(username, password);
    
    if (result.success) {
      // Hide login, show app
      document.getElementById('loginOverlay').classList.add('hidden');
      // Initialize rest of app
      initAppAfterAuth();
    } else {
      // Show error
      errorDiv.textContent = result.error || 'Login failed';
      errorDiv.classList.add('visible');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Sign in';
    }
  });
  
  // Set up bootstrap form
  setupBootstrapForm();
}

// Check if bootstrap is available
async function checkBootstrapStatus() {
  try {
    const response = await fetch('/api/auth/bootstrap/status');
    const data = await response.json();
    
    const bootstrapSection = document.getElementById('bootstrapSection');
    if (bootstrapSection && data.bootstrapAvailable) {
      bootstrapSection.style.display = 'block';
      console.log('🔧 Bootstrap available - no users exist');
    }
  } catch (error) {
    console.error('Failed to check bootstrap status:', error);
  }
}

// Set up bootstrap form
function setupBootstrapForm() {
  const form = document.getElementById('bootstrapForm');
  const errorDiv = document.getElementById('loginError');
  const submitBtn = document.getElementById('bootstrapButton');
  
  if (!form) return;
  
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    
    const username = document.getElementById('bootstrapUsername').value;
    const email = document.getElementById('bootstrapEmail').value;
    const displayName = document.getElementById('bootstrapDisplayName').value;
    const password = document.getElementById('bootstrapPassword').value;
    
    submitBtn.disabled = true;
    submitBtn.textContent = 'Creating...';
    errorDiv.classList.remove('visible');
    
    try {
      const response = await apiPost('/api/auth/bootstrap', { username, email, displayName, password });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // User created, now log them in
        errorDiv.textContent = 'User created! Logging in...';
        errorDiv.style.color = '#10b981';
        errorDiv.classList.add('visible');
        
        // Auto-login with created credentials
        const loginResult = await login(username, password);
        
        if (loginResult.success) {
          document.getElementById('loginOverlay').classList.add('hidden');
          initAppAfterAuth();
        } else {
          errorDiv.textContent = 'User created but login failed. Try signing in manually.';
          errorDiv.style.color = '';
          submitBtn.disabled = false;
          submitBtn.textContent = 'Create Admin User';
          // Hide bootstrap section since user now exists
          document.getElementById('bootstrapSection').style.display = 'none';
        }
      } else {
        errorDiv.textContent = data.error || 'Failed to create user';
        errorDiv.classList.add('visible');
        submitBtn.disabled = false;
        submitBtn.textContent = 'Create Admin User';
      }
    } catch (error) {
      console.error('Bootstrap error:', error);
      errorDiv.textContent = 'Network error';
      errorDiv.classList.add('visible');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Create Admin User';
    }
  });
}

// Initialize app after successful authentication
function initAppAfterAuth() {
  // Set up user menu
  const user = getCurrentUser();
  document.getElementById('userMenuName').textContent = user.displayName || user.username;
  
  // Set up settings button
  document.getElementById('settingsButton').addEventListener('click', () => {
    openProfileModal();
  });
  
  // Set up logout button
  document.getElementById('logoutButton').addEventListener('click', async () => {
    await logout();
    window.location.reload(); // Reset app state
  });
  
  // Role-based UI visibility
  if (isRepoOwner()) {
    // Show repo owner-only elements
    document.querySelectorAll('.repo-owner-only').forEach(el => {
      el.style.display = '';
    });
  } else {
    // Hide repo owner-only elements (ensure they're hidden)
    document.querySelectorAll('.repo-owner-only').forEach(el => {
      el.style.display = 'none';
    });
  }
  
  // Show/hide admin link for god mode users only
  if (isGodMode()) {
    document.querySelectorAll('.god-mode-only').forEach(el => {
      el.style.display = '';
    });
  } else {
    document.querySelectorAll('.god-mode-only').forEach(el => {
      el.style.display = 'none';
    });
  }
  
  // Initialize all app modules
  initFileBrowser();
  initAI();
  initSectionNav();
  initVersionBadge();
  initCommentsPanel();
  
  // Initialize history panel
  if (typeof initHistoryPanel === 'function') {
    initHistoryPanel();
  }
}

// Start app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  initApp();
});